import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';

import { AdminLayout } from './AdminLayout';
import { LazyScreen } from './components/LazyScreen';
import { RequireAuth } from './RequireAuth';

/**
 * Admin screens, each its own lazy chunk.
 *
 * § Frontend Architecture: a visitor reading the portfolio must never download the CMS.
 * The whole tree is behind `lazy()`, and the build output is checked to confirm the split
 * is real rather than assumed.
 */
const LoginPage = lazy(() => import('./LoginPage'));
const ChangePasswordPage = lazy(() => import('./ChangePasswordPage'));
const AdminOverviewPage = lazy(() => import('./AdminOverviewPage'));
const ProjectsPage = lazy(() => import('./projects/ProjectsPage'));
const ExperiencePage = lazy(() => import('./experience/ExperiencePage'));
const SkillsPage = lazy(() => import('./skills/SkillsPage'));
const MediaLibraryPage = lazy(() => import('./media/MediaLibraryPage'));
const SettingsPage = lazy(() => import('./settings/SettingsPage'));

export const adminRoutes: RouteObject[] = [
  {
    // Outside RequireAuth: signing in is what you do when you have no session.
    path: '/admin/login',
    element: (
      <LazyScreen>
        <LoginPage />
      </LazyScreen>
    ),
  },
  {
    // Inside RequireAuth but OUTSIDE AdminLayout. The layout links to sections the API
    // refuses while a password change is outstanding, so showing it here would offer
    // navigation that only produces 422s.
    path: '/admin/change-password',
    element: (
      <RequireAuth>
        <LazyScreen>
          <ChangePasswordPage />
        </LazyScreen>
      </RequireAuth>
    ),
  },
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyScreen>
            <AdminOverviewPage />
          </LazyScreen>
        ),
      },
      {
        path: 'projects',
        element: (
          <LazyScreen>
            <ProjectsPage />
          </LazyScreen>
        ),
      },
      {
        path: 'experience',
        element: (
          <LazyScreen>
            <ExperiencePage />
          </LazyScreen>
        ),
      },
      {
        path: 'skills',
        element: (
          <LazyScreen>
            <SkillsPage />
          </LazyScreen>
        ),
      },
      {
        path: 'media',
        element: (
          <LazyScreen>
            <MediaLibraryPage />
          </LazyScreen>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyScreen>
            <SettingsPage />
          </LazyScreen>
        ),
      },
    ],
  },
];
